import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Popconfirm, Modal, Button, Tooltip, Divider, Empty, Icon } from 'antd';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import * as actions from '~actions/payment';
import { communicationStatus, propertyListingForm, paymentPlanCategoryType } from '~constants';
import Pagination from '~components/ui/pagination';
import ErrorMessage from '~components/ui/error-message';
import generatePath from '~settings/routing';
import { decode } from 'js-base64';
import { isRegionalSupplyHeadRole, isSupplyRole, isAdminRole } from '~helpers/auth';

const mapStateToProps = state => {
  const payment = state.payment.get('longtail').toJS();
  return {
    list: payment.paymentPlans.edges || [],
    pageInfo: payment.paymentPlans.pageInfo || null,
    totalCount: payment.paymentPlans.totalCount || 0,
    getListCommunication: payment.getListCommunication || '',
  };
};

const mapDispatchToProps = dispatch => ({
  getList: params => {
    dispatch(actions.getLongtailPaymentPlans(params));
  },
  deleteLongtailPayment: (params, onSuccess) => {
    dispatch(actions.deleteLongtailPayment(params, onSuccess));
  },
});

@withRouter
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation(['landlord', 'cms'])
class LongtailPaymentListing extends React.Component {
  constructor() {
    super();
    this.state = {
      pageNumber: 1,
      showViewPropertiesModal: false,
      viewProperties: [],
    };
  }

  componentDidMount() {
    this.initialize();
  }

  calcPaymentPlanHasIntegratedProperty = () =>
    this.props.list?.some(item => item.node.hasIntegratedProperty);

  initialize = () => {
    const { landlordId } = this.props;
    this.props.getList({ landlordId, pageSize: 10, pageNumber: 1 }, true);
  };

  onPaginationChange = page => {
    this.setState(
      Object.assign({}, this.state, {
        pageNumber: page,
      }),
      () => {
        this.props.getList({
          pageSize: 10,
          pageNumber: this.state.pageNumber,
          landlordId: this.props?.landlordId,
        });
      },
    );
  };

  getDeposits = item => item.node.deposits;

  getFees = item => item.node.fees;

  getInstalments = item => item.node.instalments;

  handleEditBtnClick = id => {
    this.props.history.push(
      generatePath(
        'paymentEnablePayments',
        {
          landlordSlug: this.props?.landlord?.slug,
        },
        { id },
      ),
    );
  };

  handleDeletePayment = paymentId => {
    this.props.deleteLongtailPayment({ id: paymentId, landlordId: this.props?.landlordId }, () => {
      this.props.getList({
        pageSize: 10,
        pageNumber: this.state.pageNumber,
        landlordId: this.props?.landlordId,
      });
    });
  };

  showViewPropertiesModal = properties => {
    this.setState({
      viewProperties: properties,
      showViewPropertiesModal: true,
    });
  };

  hiddenViewPropertiesModal = () => {
    this.setState({
      viewProperties: [],
      showViewPropertiesModal: false,
    });
  };

  createPaymentPlan = () => {
    const { landlord } = this.props;
    this.props.history.push(generatePath('paymentInitialSetup', { landlordSlug: landlord?.slug }));
  };

  hasPublishedProperty = propeties =>
    propeties?.some(propety => propety?.status === propertyListingForm.statuses.published);

  isMustPayDepositFee = paymentPlan =>
    paymentPlan?.node?.category === paymentPlanCategoryType.DEPOSIT_IS_REQUIRED;

  render() {
    const { getListCommunication, t, totalCount, landlord } = this.props;

    const canEditRole = isAdminRole() || isSupplyRole() || isRegionalSupplyHeadRole();
    return (
      <>
        <If
          condition={landlord?.hasIntegratedProperty && this.calcPaymentPlanHasIntegratedProperty()}
        >
          <div className="long-tail-payment__tip">
            <Icon type="info-circle" theme="filled" className="info-icon" />
            <div className="tips">
              {this.props.t('landlord.property.long_tail.detail.has_integrated_property.tip')}
            </div>
          </div>
        </If>
        <div className="long-tail-payment__list">
          <div className="long-tail-payment__header">
            <div className="long-tail-payment__header__info">
              <h2 className="long-tail-payment__header__heading">
                {this.props.t('landlord.payment.long_tail.list.title')}
              </h2>
            </div>
          </div>
          <If condition={totalCount !== 0 && canEditRole}>
            <Button
              onClick={this.createPaymentPlan}
              className="long-tail-payment__header__add-btn"
              type="primary"
              size="large"
              ghost
            >
              <PlusOutlined />
              {this.props.t('landlord.payment.long_tail.list.btn.create_new_payment')}
            </Button>
          </If>
          <Choose>
            <When condition={getListCommunication.status === communicationStatus.FETCHING}>
              <span className="loading" />
            </When>
            <When condition={getListCommunication.status !== communicationStatus.IDLE}>
              <ErrorMessage
                type="middle"
                className="try-again--with-border"
                communication={getListCommunication}
                buttonText={this.props.t('landlord.dashboard.error.try_again')}
                buttonFunction={this.initialize}
                defaultErrorMessage={this.props.t('landlord.error.default')}
                clientErrorMessage={this.props.t('landlord.error.default')}
                timeoutMessage={this.props.t('landlord.error.timeout')}
              />
            </When>
            <When condition={totalCount === 0}>
              <div className="long-tail-payment__welcome">
                <Empty
                  imageStyle={{
                    height: 60,
                  }}
                  description={this.props.t('landlord.payment.long_tail.empty.desc')}
                >
                  <Button type="primary" ghost onClick={this.createPaymentPlan}>
                    <PlusOutlined />
                    {this.props.t('landlord.payment.long_tail.list.btn.create_new_payment')}
                  </Button>
                </Empty>
              </div>
            </When>
            <Otherwise>
              <div className="long-tail-payment__property-list">
                <For each="item" of={this.props.list}>
                  <div className="long-tail-payment__payment-list-box">
                    <div className="long-tail-payment__payment-list__action">
                      <div>
                        <span className="long-tail-payment__payment-list-name">
                          {item.node.name}
                        </span>

                        <If condition={item.node?.active}>
                          <span className="long-tail-payment__property-list__text--active-status">
                            {this.props.t('landlord.payment.list.column.status_value.active')}
                          </span>
                        </If>
                        <If condition={!item.node?.active}>
                          <span className="long-tail-payment__property-list__text--inactive-status">
                            {this.props.t('landlord.payment.list.column.status_value.inactive')}
                          </span>
                        </If>
                      </div>
                      <If condition={canEditRole}>
                        <div>
                          <button
                            className="long-tail-payment__property-list__link"
                            onClick={() => {
                              this.handleEditBtnClick(item.node.id);
                            }}
                          >
                            <span className="long-tail-payment__property-list__text--keppel">
                              {this.props.t('landlord.payment.long_tail.list.btn.edit')}
                            </span>
                          </button>
                          <If condition={!item.node.hasIntegratedProperty}>
                            <Divider type="vertical" className="long-tail-payment__divider" />
                            <If condition={this.hasPublishedProperty(item.node.properties)}>
                              <Tooltip
                                placement="topRight"
                                title={t('payment_plan_disabled_delete_btn_tooltip')}
                              >
                                <button className="long-tail-payment__property-list__link">
                                  <span
                                    className={classNames(
                                      'long-tail-payment__property-list__text--keppel',
                                      'long-tail-payment__property-list__text--delete',
                                      'long-tail-payment__property-list__disabled-btn',
                                    )}
                                  >
                                    {t('landlord.payment.long_tail.list.btn.delete')}
                                  </span>
                                </button>
                              </Tooltip>
                            </If>
                            <If condition={!this.hasPublishedProperty(item.node.properties)}>
                              <Popconfirm
                                placement="topRight"
                                title={t(
                                  'landlord.payment.long_tail.delete_instalment_plan.title',
                                  {
                                    name: item.node.name,
                                  },
                                )}
                                onConfirm={() => {
                                  this.handleDeletePayment(item.node.id);
                                }}
                                okText={t('landlord.payment.long_tail.button.yes')}
                                okType="danger"
                                cancelText={t('landlord.payment.long_tail.button.no')}
                                overlayStyle={{ width: 410 }}
                              >
                                <button className="long-tail-payment__property-list__link">
                                  <span
                                    className={
                                      'long-tail-payment__property-list__text--keppel long-tail-payment__property-list__text--delete '
                                    }
                                  >
                                    {t('landlord.payment.long_tail.list.btn.delete')}
                                  </span>
                                </button>
                              </Popconfirm>
                            </If>
                          </If>
                        </div>
                      </If>
                    </div>
                    <div className="long-tail-payment__payment-list__info">
                      <span className="long-tail-payment__payment-list__info-label">ID:</span>
                      {JSON.parse(decode(item.node.id))?.id ?? '-'}
                    </div>
                    <div className="long-tail-payment__payment-list__info">
                      <span className="long-tail-payment__payment-list__info-label">
                        {this.props.t('landlord.payment.list.column.deposits_or_fees')}:
                      </span>
                      <span>
                        <Choose>
                          <When
                            condition={
                              this.isMustPayDepositFee(item) &&
                              (this.getDeposits(item).length > 0 || this.getFees(item).length > 0)
                            }
                          >
                            <Icon
                              type="check"
                              className="long-tail-payment__payment-list__icon-tick"
                            />
                          </When>
                          <When
                            condition={
                              this.isMustPayDepositFee(item) &&
                              !(this.getDeposits(item).length > 0 && this.getFees(item).length > 0)
                            }
                          >
                            <Icon type="close" style={{ fontSize: '12px' }} />
                          </When>
                          <When
                            condition={
                              !this.isMustPayDepositFee(item) &&
                              (this.getDeposits(item).length > 0 || this.getFees(item).length > 0)
                            }
                          >
                            <Icon
                              type="check"
                              className="long-tail-payment__payment-list__icon-tick"
                            />
                          </When>
                          <When
                            condition={
                              !this.isMustPayDepositFee(item) &&
                              (this.getDeposits(item).length === 0 ||
                                this.getFees(item).length === 0)
                            }
                          >
                            -
                          </When>
                        </Choose>
                      </span>
                    </div>
                    <div className="long-tail-payment__payment-list__info">
                      <span className="long-tail-payment__payment-list__info-label">
                        {this.props.t('landlord.payment.list.column.rent_payment')}:
                      </span>
                      <span>
                        <Choose>
                          <When condition={this.getInstalments(item).length > 0}>
                            <Icon
                              type="check"
                              className="long-tail-payment__payment-list__icon-tick"
                            />
                          </When>
                          <Otherwise>
                            <Icon type="close" style={{ fontSize: '12px' }} />
                          </Otherwise>
                        </Choose>
                      </span>
                    </div>
                    <div className="long-tail-payment__payment-list__info">
                      <span className="long-tail-payment__payment-list__info-label">
                        {t('landlord.payment.long_tail.initial_setup.label.applied_properties')}:
                      </span>
                      <If condition={item.node.properties.length === 0}>{'-'}</If>
                      <If condition={item.node.properties.length > 0}>
                        {item.node.properties.length}
                        <button
                          className="long-tail-payment__property-list__link"
                          onClick={() => {
                            this.showViewPropertiesModal(item.node.properties);
                          }}
                        >
                          <span className="long-tail-payment__property-list__text--keppel">
                            {this.props.t('landlord.payment.long_tail.list.btn.view')}
                          </span>
                        </button>
                      </If>
                    </div>
                  </div>
                </For>
              </div>

              <If condition={Math.ceil(this.props.totalCount / 10) > 1}>
                <div
                  className="dashboard__pagination"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                >
                  <Pagination
                    currentPage={this.state.pageNumber}
                    totalPage={Math.ceil(this.props.totalCount / 10)}
                    onPageClick={this.onPaginationChange}
                  />
                </div>
              </If>
            </Otherwise>
          </Choose>

          <Modal
            title={t('landlord.payment.long_tail.initial_setup.label.applied_properties')}
            visible={this.state.showViewPropertiesModal}
            onCancel={this.hiddenViewPropertiesModal}
            footer={null}
            centered
            wrapClassName="small-ant-modal"
          >
            <For of={this.state.viewProperties} each="property">
              <p className="long-tail-payment____property-modal__name" key={property.slug}>
                {property.name}
              </p>
            </For>
          </Modal>
        </div>
      </>
    );
  }
}

export default LongtailPaymentListing;
